import { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { format } from 'date-fns';
import { colors } from '@src/styles';
import { InfoField } from './InfoField';
import { TabButton } from './TabButton';
import { safeObject } from '../../utils/safeObject';
import { JsonViewer } from '../JsonViewer';
import { ActionDTO } from '@src/services/tools/modules/v1/scripts/dto/tools-requests.dto';

interface ActionDetailsContentProps {
  actionData: ActionDTO;
}

const formatDateTime = (date?: string | null): string => (date ? format(new Date(date), 'dd/MM/yyyy HH:mm:ss') : '—');

const formatDuration = (startedAt?: string, finishedAt?: string): string => {
  if (!startedAt || !finishedAt) return '—';
  const milliseconds = new Date(finishedAt).getTime() - new Date(startedAt).getTime();
  if (milliseconds < 0) return '—';
  if (milliseconds < 1000) return `${milliseconds} ms`;
  if (milliseconds < 60000) return `${(milliseconds / 1000).toFixed(1)} s`;
  const minutes = Math.floor(milliseconds / 60000);
  return `${minutes} min ${Math.round((milliseconds % 60000) / 1000)} s`;
};

export const ActionDetailsContent = ({ actionData }: ActionDetailsContentProps) => {
  const [activeTab, setActiveTab] = useState<'body' | 'response'>('body');
  const [hasCopied, setHasCopied] = useState(false);

  const activeContent = activeTab === 'body' ? safeObject(actionData.body) : safeObject(actionData.response);
  const isEmpty = Object.keys(activeContent).length === 0;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(activeContent, null, 2));
      setHasCopied(true);
      window.setTimeout(() => setHasCopied(false), 1500);
    } catch (error) {
      console.error('Failed to copy JSON:', error);
    }
  };

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2.5 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' },
          gap: 2,
          mb: 2.5,
        }}
      >
        <InfoField label="Criado" value={formatDateTime(actionData.createdAt)} />
        <InfoField label="Iniciado" value={formatDateTime(actionData.startedAt)} />
        <InfoField label="Finalizado" value={formatDateTime(actionData.finishedAt)} />
        <InfoField label="Duração" value={formatDuration(actionData.startedAt, actionData.finishedAt)} />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
        <Box
          role="tablist"
          aria-label="Conteúdo da action"
          sx={{
            display: 'inline-flex',
            gap: '2px',
            p: '3px',
            borderRadius: '8px',
            border: `1px solid ${colors.brand.line}`,
            backgroundColor: colors.background.primary,
          }}
        >
          <TabButton label="Body" isActive={activeTab === 'body'} onClick={() => setActiveTab('body')} />
          <TabButton label="Response" isActive={activeTab === 'response'} onClick={() => setActiveTab('response')} />
        </Box>
        <Tooltip title={hasCopied ? 'Copiado!' : 'Copiar JSON'} arrow>
          <span>
            <IconButton
              onClick={handleCopy}
              disabled={isEmpty}
              aria-label={`Copiar ${activeTab === 'body' ? 'body' : 'response'}`}
              sx={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                color: hasCopied ? colors.status.concluded : colors.brand.textSecondary,
                '& svg': { color: 'inherit', fontSize: 16 },
                '&:hover': { color: colors.primary.yellow, backgroundColor: 'rgba(255, 160, 21, 0.1)' },
              }}
            >
              <ContentCopyIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box
        role="tabpanel"
        sx={{
          height: 'min(360px, 42vh)',
          overflow: 'auto',
          padding: '12px 14px',
          borderRadius: '12px',
          border: `1px solid ${colors.brand.line}`,
          backgroundColor: colors.background.primary,
        }}
      >
        {isEmpty ? (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: colors.brand.textSecondary }}>
              {activeTab === 'body' ? 'Esta action não tem body.' : 'Ainda não há response para esta action.'}
            </Typography>
          </Box>
        ) : (
          <JsonViewer data={activeContent} />
        )}
      </Box>
    </Box>
  );
};
